import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { UserIcon, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-02-19 (Dave) — self-serve name edit. Every logged-in user can
// fix their own name; email / role / memberships remain admin-only.
// Trigger: `window.dispatchEvent(new CustomEvent("ureserve:open-edit-name"))`
// from anywhere (e.g. the Profile item in the user pill dropdown).
export default function EditNameModal({ open, currentName, onClose, onSaved }) {
  const [draft, setDraft] = useState(currentName || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDraft(currentName || "");
      setErr("");
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open, currentName]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const clean = draft.trim();
  const unchanged = clean === (currentName || "").trim();
  const canSave = !!clean && !unchanged && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setErr("");
    try {
      const r = await axios.patch(`${API}/users/me`, { full_name: clean });
      onSaved?.(r.data.full_name || clean);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={onClose}
      data-testid="edit-name-modal"
    >
      <div
        className="bg-white border-2 border-navy shadow-2xl w-[440px] max-w-[92vw] rounded-[8px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule bg-navy-wash/40">
          <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy flex items-center gap-2">
            <UserIcon size={13} /> Edit Name
          </div>
          <button
            onClick={onClose}
            className="text-slate hover:text-navy"
            data-testid="edit-name-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
              Full name
            </span>
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              maxLength={120}
              disabled={saving}
              data-testid="edit-name-input"
              className="border border-rule rounded-[3px] px-2 py-1.5 text-[13px] focus:outline-none focus:border-navy"
            />
            <span className="text-[10.5px] text-slate italic">
              Your email address stays the same; only your display name changes.
            </span>
          </label>
          {err && (
            <div
              className="text-[11.5px] text-coral border border-coral bg-coral-wash rounded-[3px] px-2 py-1"
              data-testid="edit-name-error"
            >
              {err}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 text-[12px] font-semibold text-slate border border-rule rounded-[3px] hover:border-navy hover:text-navy disabled:opacity-50"
              data-testid="edit-name-cancel"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={!canSave}
              className="px-3 py-1.5 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="edit-name-save"
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
